import { createFileRoute, notFound } from "@tanstack/react-router";
import SellBrandPage from "@/pages/SellBrandPage";
import { listingsV2QueryOptions } from "@/lib/queries/publicData";
import { fxRatesQueryOptions } from "@/lib/fxRates";
import {
  SELL_BRAND_SLUG_TO_MAKE,
  sellBrandStats,
  sellBrandIndexable,
  sellBrandCanonical,
  sellBrandSeo,
  type SellBrandStats,
} from "@/lib/sellBrandPages";

export const Route = createFileRoute("/vinde-masina/$marca")({
  loader: async ({ context, params }) => {
    const make = SELL_BRAND_SLUG_TO_MAKE[params.marca];
    if (!make) throw notFound();
    const [rows, rates] = await Promise.all([
      context.queryClient
        .ensureQueryData(listingsV2QueryOptions({ country_code: "RO", make, limit: 2000 }))
        .catch(() => []),
      context.queryClient.ensureQueryData(fxRatesQueryOptions()).catch(() => null),
    ]);
    return sellBrandStats(make, Array.isArray(rows) ? rows : [], rates);
  },
  head: ({ loaderData }) => {
    const stats = loaderData as SellBrandStats | undefined;
    if (!stats) {
      return {
        meta: [{ title: "Vinde mașina ta | RO Auto Market" }, { name: "robots", content: "noindex, follow" }],
        links: [],
      };
    }
    const { title, description } = sellBrandSeo(stats);
    const canonical = sellBrandCanonical(stats.make);
    const indexable = sellBrandIndexable(stats);
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Acasă", item: "https://roautomarket.ro/" },
        { "@type": "ListItem", position: 2, name: "Vinde mașina", item: "https://roautomarket.ro/vinde-masina" },
        { "@type": "ListItem", position: 3, name: stats.make, item: canonical },
      ],
    };
    return {
      meta: [
        { title },
        { name: "title", content: title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:locale", content: "ro_RO" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...(indexable ? [] : [{ name: "robots", content: "noindex, follow" }]),
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [{ type: "application/ld+json", children: JSON.stringify(breadcrumb) }],
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const stats = Route.useLoaderData();
  return <SellBrandPage stats={stats} />;
}
